/**
 * Bunamo Flavor Tree
 * Expandable left-to-right tree with product results.
 */
(function () {
  'use strict';

  function normalize(text) {
    return String(text || '').toLowerCase().trim();
  }

  function FlavorTree(sectionId) {
    this.sectionId = sectionId;
    this.container = document.getElementById('flavor-tree-container-' + sectionId);
    this.connectionsEl = document.getElementById('flavor-tree-connections-' + sectionId);
    this.realmsEl = document.getElementById('flavor-tree-realms-' + sectionId);
    this.categoriesEl = document.getElementById('flavor-tree-categories-' + sectionId);
    this.flavorsEl = document.getElementById('flavor-tree-flavors-' + sectionId);
    this.productsGrid = document.getElementById('flavor-tree-products-grid-' + sectionId);
    this.paginationEl = document.getElementById('flavor-tree-products-pagination-' + sectionId);
    this.allButtonEl = document.getElementById('flavor-tree-all-button-' + sectionId);

    this.root = null;
    this.selectedRealm = null;
    this.selectedCategory = null;
    this.selectedFlavor = null;
    this.selectedFlavorColor = null;
    this.allResults = [];
    this.currentPage = 0;
    this.productsPerPage = 1;
    this._connectionsRaf = null;
    this._hoverRaf = null;
    this._hoverListEl = null;
    this._hoverMouseListY = 0;
    this._previousListOrders = {
      realms: [],
      categories: [],
      flavors: []
    };
    this._transferDurationMs = 150;
    this._flavorProductSets = {};

    this.init();
  }

  FlavorTree.prototype.init = function () {
    var hierarchy = window.BunamoTasteData && window.BunamoTasteData.FLAVOR_HIERARCHY;
    if (!hierarchy || !hierarchy.children) {
      this.realmsEl.innerHTML = '<p class="flavor-tree-empty">Keine Kategorien gefunden</p>';
      return;
    }

    this.root = this.filterTreeByAvailableFlavors(hierarchy);
    if (!this.root || !this.root.children || this.root.children.length === 0) {
      this.realmsEl.innerHTML = '<p class="flavor-tree-empty">Keine Kategorien gefunden</p>';
      this.categoriesEl.innerHTML = '<p class="flavor-tree-empty">Keine Kategorien gefunden</p>';
      this.flavorsEl.innerHTML = '<p class="flavor-tree-empty">Keine Geschmäcker gefunden</p>';
      return;
    }

    this.buildFlavorProductSets();
    this.annotateNodeMatchCounts(this.root);

    this.renderRealms();
    this.openFirstRealm();
    this.setupConnectionListeners();
    this.setupParabolaHover();
    this.scheduleInitialConnectionDraw();
  };

  FlavorTree.prototype.scheduleInitialConnectionDraw = function () {
    var self = this;

    // Draw once now and again after layout settles so initial lines are reliable.
    this.requestConnectionsUpdate();
    requestAnimationFrame(function () {
      self.requestConnectionsUpdate();
      requestAnimationFrame(function () {
        self.requestConnectionsUpdate();
      });
    });

    setTimeout(function () {
      self.requestConnectionsUpdate();
    }, 120);
  };

  FlavorTree.prototype.filterTreeByAvailableFlavors = function (node) {
    var available = window.BUNAMO_AVAILABLE_FLAVORS || new Set();
    var availableSet = new Set();

    available.forEach(function (name) {
      availableSet.add(normalize(name));
    });

    function hasProducts(name) {
      return availableSet.has(normalize(name));
    }

    function filterNode(current) {
      if (!current.children || current.children.length === 0) {
        return hasProducts(current.name) ? Object.assign({}, current) : null;
      }

      var filteredChildren = current.children
        .map(filterNode)
        .filter(function (child) { return child !== null; });

      if (filteredChildren.length > 0) {
        return Object.assign({}, current, { children: filteredChildren });
      }

      return hasProducts(current.name) ? Object.assign({}, current, { children: [] }) : null;
    }

    if (node.name === 'ROOT') {
      return {
        name: 'ROOT',
        children: node.children.map(filterNode).filter(function (child) { return child !== null; })
      };
    }

    return filterNode(node);
  };

  FlavorTree.prototype.buildFlavorProductSets = function () {
    var products = window.BUNAMO_PRODUCTS || [];
    var sets = {};

    products.forEach(function (product, index) {
      if (!product || !Array.isArray(product.tasteNotes)) return;
      var productId = product.id || product.handle || ('product-' + index);

      product.tasteNotes.forEach(function (flavor) {
        var key = normalize(flavor);
        if (!key) return;
        if (!sets[key]) sets[key] = new Set();
        sets[key].add(productId);
      });
    });

    this._flavorProductSets = sets;
  };

  FlavorTree.prototype.getProductSetForFlavor = function (name) {
    var key = normalize(name);
    if (!key) return new Set();
    return this._flavorProductSets[key] || new Set();
  };

  FlavorTree.prototype.getFlavorMatchCount = function (name) {
    return this.getProductSetForFlavor(name).size;
  };

  FlavorTree.prototype.annotateNodeMatchCounts = function (node) {
    var self = this;
    if (!node) return new Set();

    var combined = new Set(this.getProductSetForFlavor(node.name));
    var children = node.children || [];

    children.forEach(function (child) {
      var childSet = self.annotateNodeMatchCounts(child);
      childSet.forEach(function (id) {
        combined.add(id);
      });
    });

    node._matchCount = combined.size;
    return combined;
  };

  FlavorTree.prototype.formatLabelWithCount = function (name, count) {
    var safeCount = typeof count === 'number' ? count : 0;
    return String(name || '') + ' (' + safeCount + ')';
  };

  FlavorTree.prototype.openFirstRealm = function () {
    var realms = this.root.children || [];
    var selectedRealmNode = realms.length
      ? realms[Math.floor(Math.random() * realms.length)]
      : null;
    if (!selectedRealmNode) return;

    this.selectedRealm = selectedRealmNode.name;
    var categories = selectedRealmNode.children || [];
    var selectedCategoryNode = categories.length
      ? categories[Math.floor(Math.random() * categories.length)]
      : null;
    this.selectedCategory = selectedCategoryNode ? selectedCategoryNode.name : null;
    this.selectedFlavor = null;
    this.selectedFlavorColor = null;

    this.renderRealms();
    this.renderCategoriesForRealm(selectedRealmNode);
    if (selectedCategoryNode) {
      var activeCategoryButton = this.categoriesEl.querySelector('.flavor-tree-item.is-active');
      this.renderFlavorsForCategory(
        selectedCategoryNode,
        selectedCategoryNode.color || selectedRealmNode.color || '#000000',
        activeCategoryButton
      );
      this.fetchProductsForFlavorNames(
        this.getLeafFlavorNamesForNode(selectedCategoryNode),
        'Suche passende Kaffees...'
      );
    } else {
      this.fetchProductsForFlavorNames(
        this.getLeafFlavorNamesForNode(selectedRealmNode),
        'Suche passende Kaffees...'
      );
    }
    this.requestConnectionsUpdate();
  };

  FlavorTree.prototype.renderRealms = function () {
    var self = this;
    var previousScrollTop = this.getListScrollTop(this.realmsEl);
    var ordered = this.getCenteredCircularItems(
      this.root.children || [],
      this.selectedRealm,
      function (realm) { return realm.name; }
    );
    var transferKeys = this.getWrappedTransferKeys(this._previousListOrders.realms, ordered.order);
    this._previousListOrders.realms = ordered.order.slice();
    this.realmsEl.innerHTML = '';

    ordered.items.forEach(function (realm) {
      var node = document.createElement('div');
      node.className = 'flavor-tree-node';

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'flavor-tree-item';
      button.setAttribute('data-item-key', normalize(realm.name));
      if (self.selectedRealm === realm.name) button.classList.add('is-active');

      var label = document.createElement('span');
      label.className = 'flavor-tree-item__label';
      label.textContent = self.formatLabelWithCount(realm.name, realm._matchCount);

      var chevron = document.createElement('span');
      chevron.className = 'flavor-tree-item__chevron';
      chevron.textContent = '';

      button.appendChild(label);
      button.appendChild(chevron);
      button.addEventListener('click', function (event) {
        self.captureHoverFromPointer(event, self.realmsEl);
        self.selectedRealm = realm.name;
        self.selectedCategory = null;
        self.selectedFlavor = null;
        self.selectedFlavorColor = null;
        self.renderRealms();
        self.renderCategoriesForRealm(realm);
        self.fetchProductsForFlavorNames(
          self.getLeafFlavorNamesForNode(realm),
          'Suche passende Kaffees...'
        );
        self.requestConnectionsUpdate();
      });

      node.appendChild(button);
      self.realmsEl.appendChild(node);
    });

    this.animateTransferredItems(this.realmsEl, transferKeys);
    this.restoreListScrollTop(this.realmsEl, previousScrollTop);
    if (this._hoverListEl === this.realmsEl) this.requestParabolaHoverUpdate();
  };

  FlavorTree.prototype.renderCategoriesForRealm = function (realm) {
    var self = this;
    var previousCategoriesScrollTop = this.getListScrollTop(this.categoriesEl);
    var previousFlavorsScrollTop = this.getListScrollTop(this.flavorsEl);
    var ordered = this.getCenteredCircularItems(
      realm.children || [],
      this.selectedCategory,
      function (category) { return category.name; }
    );
    var transferKeys = this.getWrappedTransferKeys(this._previousListOrders.categories, ordered.order);
    this._previousListOrders.categories = ordered.order.slice();
    this.categoriesEl.innerHTML = '';
    this.flavorsEl.innerHTML = '<p class="flavor-tree-empty">Wähle eine Kategorie aus</p>';
    this._previousListOrders.flavors = [];

    ordered.items.forEach(function (category) {
      var node = document.createElement('div');
      node.className = 'flavor-tree-node';

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'flavor-tree-item';
      button.setAttribute('data-item-key', normalize(category.name));
      if (self.selectedCategory === category.name) button.classList.add('is-active');

      var label = document.createElement('span');
      label.className = 'flavor-tree-item__label';
      label.textContent = self.formatLabelWithCount(category.name, category._matchCount);

      var chevron = document.createElement('span');
      chevron.className = 'flavor-tree-item__chevron';
      chevron.textContent = '';

      button.appendChild(label);
      button.appendChild(chevron);
      button.addEventListener('click', function (event) {
        self.captureHoverFromPointer(event, self.categoriesEl);
        self.selectedCategory = category.name;
        self.selectedFlavor = null;
        self.selectedFlavorColor = null;
        self.renderCategoriesForRealm(realm);
        var activeCategoryButton = self.categoriesEl.querySelector('.flavor-tree-item.is-active');
        self.renderFlavorsForCategory(category, category.color || realm.color || '#000000', activeCategoryButton);
        self.fetchProductsForFlavorNames(
          self.getLeafFlavorNamesForNode(category),
          'Suche passende Kaffees...'
        );
        self.requestConnectionsUpdate();
      });

      node.appendChild(button);
      self.categoriesEl.appendChild(node);
    });

    this.animateTransferredItems(this.categoriesEl, transferKeys);
    this.restoreListScrollTop(this.categoriesEl, previousCategoriesScrollTop);
    this.restoreListScrollTop(this.flavorsEl, previousFlavorsScrollTop);
    if (this._hoverListEl === this.categoriesEl) this.requestParabolaHoverUpdate();
    this.alignChildColumnToParent(
      this.realmsEl.querySelector('.flavor-tree-item.is-active'),
      this.categoriesEl
    );
    this.requestConnectionsUpdate();
  };

  FlavorTree.prototype.flattenLeafFlavors = function (nodes, inheritedColor, acc) {
    var self = this;
    (nodes || []).forEach(function (node) {
      var color = node.color || inheritedColor || '#000000';
      if (!node.children || node.children.length === 0) {
        acc.push({ name: node.name, color: color });
        return;
      }
      self.flattenLeafFlavors(node.children, color, acc);
    });
  };

  FlavorTree.prototype.renderFlavorsForCategory = function (category, fallbackColor, alignToButton) {
    var self = this;
    var previousScrollTop = this.getListScrollTop(this.flavorsEl);
    var leaves = [];
    this.flattenLeafFlavors(category.children || [], fallbackColor, leaves);

    if (leaves.length === 0 && category.name) {
      leaves.push({ name: category.name, color: fallbackColor || '#000000' });
    }

    var ordered = this.getCenteredCircularItems(
      leaves,
      this.selectedFlavor,
      function (leaf) { return leaf.name; }
    );
    var transferKeys = this.getWrappedTransferKeys(this._previousListOrders.flavors, ordered.order);
    this._previousListOrders.flavors = ordered.order.slice();
    this.flavorsEl.innerHTML = '';

    ordered.items.forEach(function (leaf) {
      var node = document.createElement('div');
      node.className = 'flavor-tree-node';

      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'flavor-tree-item';
      button.setAttribute('data-item-key', normalize(leaf.name));
      if (self.selectedFlavor === leaf.name) button.classList.add('is-active');

      var label = document.createElement('span');
      label.className = 'flavor-tree-item__label';
      label.textContent = self.formatLabelWithCount(leaf.name, self.getFlavorMatchCount(leaf.name));

      var chevron = document.createElement('span');
      chevron.className = 'flavor-tree-item__chevron';
      chevron.textContent = '';

      button.appendChild(label);
      button.appendChild(chevron);
      button.addEventListener('click', function (event) {
        self.captureHoverFromPointer(event, self.flavorsEl);
        self.selectedFlavor = leaf.name;
        self.selectedFlavorColor = leaf.color;
        self.currentPage = 0;
        self.fetchProducts();
        self.renderFlavorsForCategory(category, fallbackColor, alignToButton);
        self.requestConnectionsUpdate();
      });

      node.appendChild(button);
      self.flavorsEl.appendChild(node);
    });

    this.animateTransferredItems(this.flavorsEl, transferKeys);
    this.restoreListScrollTop(this.flavorsEl, previousScrollTop);
    if (this._hoverListEl === this.flavorsEl) this.requestParabolaHoverUpdate();
    this.alignChildColumnToParent(
      alignToButton || this.categoriesEl.querySelector('.flavor-tree-item.is-active'),
      this.flavorsEl
    );
    this.requestConnectionsUpdate();
  };

  FlavorTree.prototype.clearProducts = function () {
    this.renderEmptyTile('Wähle einen Geschmack, um passende Kaffees zu sehen');
    BunamoUtils.renderPagination(this.paginationEl, 0, 0, null, null);
    this.renderAllButton(0);
    this.requestConnectionsUpdate();
  };

  FlavorTree.prototype.getLeafFlavorNamesForNode = function (node) {
    var names = [];

    function collect(current) {
      if (!current) return;
      var children = current.children || [];
      if (!children.length) {
        names.push(current.name);
        return;
      }
      children.forEach(collect);
    }

    collect(node);
    return names;
  };

  FlavorTree.prototype.fetchProductsForFlavorNames = function (flavorNames, loadingMessage) {
    var normalizedFlavorSet = new Set(
      (flavorNames || []).map(function (name) { return normalize(name); }).filter(Boolean)
    );

    if (!normalizedFlavorSet.size) {
      this.allResults = [];
      this.currentPage = 0;
      this.renderEmptyTile('Keine Kaffees gefunden');
      BunamoUtils.renderPagination(this.paginationEl, 0, 0, null, null);
      this.renderAllButton(0);
      return;
    }

    this.renderLoadingTile(loadingMessage || 'Suche passende Kaffees...');

    var products = (window.BUNAMO_PRODUCTS || []).filter(function (product) {
      if (!product || !Array.isArray(product.tasteNotes)) return false;
      return product.tasteNotes.some(function (flavor) {
        return normalizedFlavorSet.has(normalize(flavor));
      });
    });

    this.allResults = this.shuffleArray(products || []);
    this.currentPage = 0;
    this.displayResults();
  };

  FlavorTree.prototype.setupConnectionListeners = function () {
    var self = this;
    window.addEventListener('resize', function () {
      self.realignColumns();
      self.requestConnectionsUpdate();
    }, { passive: true });

    [this.realmsEl, this.categoriesEl, this.flavorsEl].forEach(function (el) {
      if (!el) return;
      el.addEventListener('scroll', function () {
        self.requestConnectionsUpdate();
      }, { passive: true });
    });
  };

  FlavorTree.prototype.setupParabolaHover = function () {
    var self = this;
    [this.realmsEl, this.categoriesEl, this.flavorsEl].forEach(function (listEl) {
      if (!listEl) return;

      listEl.addEventListener('mousemove', function (event) {
        var listRect = listEl.getBoundingClientRect();
        self._hoverListEl = listEl;
        self._hoverMouseListY = (event.clientY - listRect.top) + listEl.scrollTop;
        self.requestParabolaHoverUpdate();
      }, { passive: true });

      listEl.addEventListener('mouseleave', function () {
        self.resetParabolaHover(listEl);
      }, { passive: true });
    });
  };

  FlavorTree.prototype.requestParabolaHoverUpdate = function () {
    var self = this;
    if (this._hoverRaf) return;
    this._hoverRaf = requestAnimationFrame(function () {
      self._hoverRaf = null;
      self.applyParabolaHover();
    });
  };

  FlavorTree.prototype.captureHoverFromPointer = function (event, listEl) {
    if (!event || !listEl) return;
    var listRect = listEl.getBoundingClientRect();
    this._hoverListEl = listEl;
    this._hoverMouseListY = (event.clientY - listRect.top) + listEl.scrollTop;
  };

  FlavorTree.prototype.applyParabolaHover = function () {
    var listEl = this._hoverListEl;
    if (!listEl) return;

    var buttons = listEl.querySelectorAll('.flavor-tree-item');
    if (!buttons.length) return;

    var influenceRadius = 72;
    var maxScaleBoost = 0.055;

    buttons.forEach(function (button) {
      var centerY = button.offsetTop + (button.offsetHeight / 2);
      var distance = Math.abs(centerY - this._hoverMouseListY);
      var normalized = Math.min(1, distance / influenceRadius);
      var intensity = 1 - (normalized * normalized); // Parabola falloff
      var scale = 1 + (Math.max(0, intensity) * maxScaleBoost);
      button.style.transform = 'scale(' + scale.toFixed(3) + ')';
    }, this);
  };

  FlavorTree.prototype.resetParabolaHover = function (listEl) {
    if (!listEl) return;
    listEl.querySelectorAll('.flavor-tree-item').forEach(function (button) {
      button.style.transform = '';
    });
    if (this._hoverListEl === listEl) this._hoverListEl = null;
  };

  FlavorTree.prototype.realignColumns = function () {
    this.alignChildColumnToParent(
      this.realmsEl.querySelector('.flavor-tree-item.is-active'),
      this.categoriesEl
    );
    this.alignChildColumnToParent(
      this.categoriesEl.querySelector('.flavor-tree-item.is-active'),
      this.flavorsEl
    );
  };

  FlavorTree.prototype.alignChildColumnToParent = function (parentButton, childListEl) {
    if (!childListEl) return;

    var basePadding = 8;
    childListEl.style.paddingTop = basePadding + 'px';
    childListEl.style.paddingBottom = basePadding + 'px';
  };

  FlavorTree.prototype.getListScrollTop = function (listEl) {
    if (!listEl) return 0;
    return listEl.scrollTop || 0;
  };

  FlavorTree.prototype.restoreListScrollTop = function (listEl, scrollTop) {
    if (!listEl || typeof scrollTop !== 'number') return;
    listEl.scrollTop = scrollTop;
  };

  FlavorTree.prototype.getCenteredCircularItems = function (items, selectedName, nameGetter) {
    var source = Array.isArray(items) ? items.slice() : [];
    var length = source.length;
    var getName = typeof nameGetter === 'function' ? nameGetter : function (item) { return item && item.name; };

    if (length <= 1) {
      return {
        items: source,
        order: source.map(function (item) { return normalize(getName(item)); })
      };
    }

    var selectedIndex = -1;
    var normalizedSelected = normalize(selectedName);
    if (normalizedSelected) {
      selectedIndex = source.findIndex(function (item) {
        return normalize(getName(item)) === normalizedSelected;
      });
    }
    if (selectedIndex < 0) selectedIndex = 0;

    var centerIndex = Math.floor((length - 1) / 2);
    var offset = centerIndex - selectedIndex;
    var orderedItems = [];

    for (var i = 0; i < length; i += 1) {
      var oldIndex = i - offset;
      while (oldIndex < 0) oldIndex += length;
      oldIndex = oldIndex % length;
      orderedItems.push(source[oldIndex]);
    }

    return {
      items: orderedItems,
      order: orderedItems.map(function (item) { return normalize(getName(item)); })
    };
  };

  FlavorTree.prototype.getWrappedTransferKeys = function (previousOrder, nextOrder) {
    if (!Array.isArray(previousOrder) || !Array.isArray(nextOrder)) return [];
    if (!previousOrder.length || previousOrder.length !== nextOrder.length) return [];

    var prev = previousOrder.slice();
    var next = nextOrder.slice();
    var sortedPrev = prev.slice().sort();
    var sortedNext = next.slice().sort();
    for (var s = 0; s < sortedPrev.length; s += 1) {
      if (sortedPrev[s] !== sortedNext[s]) return [];
    }

    var previousIndexMap = {};
    prev.forEach(function (key, idx) {
      previousIndexMap[key] = idx;
    });

    var threshold = next.length / 2;
    var wrapped = [];

    next.forEach(function (key, newIndex) {
      var oldIndex = previousIndexMap[key];
      if (typeof oldIndex !== 'number') return;
      var delta = newIndex - oldIndex;
      if (Math.abs(delta) > threshold) wrapped.push(key);
    });

    return wrapped;
  };

  FlavorTree.prototype.animateTransferredItems = function (listEl, transferKeys) {
    if (!listEl || !transferKeys || !transferKeys.length) return;
    var duration = this._transferDurationMs || 150;

    var transferKeySet = {};
    transferKeys.forEach(function (key) {
      transferKeySet[key] = true;
    });

    var buttons = listEl.querySelectorAll('.flavor-tree-item[data-item-key]');
    var hasTransferItems = false;
    buttons.forEach(function (button) {
      var key = button.getAttribute('data-item-key');
      if (!transferKeySet[key]) return;
      hasTransferItems = true;
      button.classList.add('flavor-tree-transfer');
    });

    if (!hasTransferItems) return;

    requestAnimationFrame(function () {
      buttons.forEach(function (button) {
        if (button.classList.contains('flavor-tree-transfer')) {
          button.classList.add('flavor-tree-transfer-active');
        }
      });

      setTimeout(function () {
        buttons.forEach(function (button) {
          button.classList.remove('flavor-tree-transfer', 'flavor-tree-transfer-active');
        });
      }, duration + 20);
    });
  };

  FlavorTree.prototype.requestConnectionsUpdate = function () {
    var self = this;
    if (!this.connectionsEl || !this.container) return;
    if (this._connectionsRaf) cancelAnimationFrame(this._connectionsRaf);
    this._connectionsRaf = requestAnimationFrame(function () {
      self._connectionsRaf = null;
      self.drawConnections();
    });
  };

  FlavorTree.prototype.drawConnections = function () {
    if (!this.connectionsEl || !this.container) return;

    var layoutRect = this.container.getBoundingClientRect();
    this.connectionsEl.setAttribute('viewBox', '0 0 ' + layoutRect.width + ' ' + layoutRect.height);
    this.connectionsEl.innerHTML = '';

    var selectedRealm = this.container.querySelector('.flavor-tree-panel--realms .flavor-tree-item.is-active');
    var categoryTargets = this.container.querySelectorAll('.flavor-tree-panel--categories .flavor-tree-item');
    if (selectedRealm && categoryTargets.length) {
      this.drawConnectionFan(selectedRealm, categoryTargets);
    }

    var selectedCategory = this.container.querySelector('.flavor-tree-panel--categories .flavor-tree-item.is-active');
    var flavorTargets = this.container.querySelectorAll('.flavor-tree-panel--flavors .flavor-tree-item');
    if (selectedCategory && flavorTargets.length) {
      this.drawConnectionFan(selectedCategory, flavorTargets);
    }
  };

  FlavorTree.prototype.drawConnectionFan = function (fromEl, targetNodeList) {
    var layoutRect = this.container.getBoundingClientRect();
    var fromRect = fromEl.getBoundingClientRect();
    var startX = fromRect.right - layoutRect.left;
    var startY = fromRect.top - layoutRect.top + (fromRect.height / 2);
    var self = this;

    targetNodeList.forEach(function (toEl) {
      var toRect = toEl.getBoundingClientRect();
      var endX = toRect.left - layoutRect.left;
      var endY = toRect.top - layoutRect.top + (toRect.height / 2);

      if (endX <= startX) return;

      var distance = endX - startX;
      var curve = Math.max(26, distance * 0.35);
      var d = [
        'M', startX, startY,
        'C', startX + curve, startY, endX - curve, endY, endX, endY
      ].join(' ');

      var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      var isSelectedPath = toEl.classList.contains('is-active');
      path.setAttribute('d', d);
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke', isSelectedPath ? '#8F8F8F' : '#D5D5D5');
      path.setAttribute('stroke-width', isSelectedPath ? '3' : '1.5');
      path.setAttribute('stroke-linecap', 'round');
      path.setAttribute('opacity', isSelectedPath ? '1' : '0.9');
      self.connectionsEl.appendChild(path);
    });
  };

  FlavorTree.prototype.renderEmptyTile = function (message) {
    this.productsGrid.innerHTML =
      '<div class="quiz-product-card flavor-tree-empty-tile">' +
      '<div class="flavor-tree-empty-tile__content">' +
      '<p class="products__empty">' + BunamoUtils.escapeHtml(message) + '</p>' +
      '</div>' +
      '</div>';
  };

  FlavorTree.prototype.renderLoadingTile = function (message) {
    this.productsGrid.innerHTML =
      '<div class="quiz-product-card flavor-tree-empty-tile">' +
      '<div class="flavor-tree-empty-tile__content">' +
      '<p class="products__loading">' + BunamoUtils.escapeHtml(message) + '</p>' +
      '</div>' +
      '</div>';
  };

  FlavorTree.prototype.shuffleArray = function (arr) {
    var a = arr.slice();
    for (var i = a.length - 1; i > 0; i -= 1) {
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = a[i];
      a[i] = a[j];
      a[j] = tmp;
    }
    return a;
  };

  FlavorTree.prototype.getContrastColor = function (hex) {
    var clean = String(hex || '#1A1A1A').replace('#', '');
    if (clean.length !== 6) return '#FFFFFF';

    var r = parseInt(clean.substring(0, 2), 16);
    var g = parseInt(clean.substring(2, 4), 16);
    var b = parseInt(clean.substring(4, 6), 16);
    var luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return luminance > 0.55 ? '#1A1A1A' : '#FFFFFF';
  };

  FlavorTree.prototype.fetchProducts = function () {
    var self = this;
    if (!this.selectedFlavor) return;

    this.renderLoadingTile('Suche passende Kaffees...');

    window.ShopifyAPI.searchByFlavor(this.selectedFlavor)
      .then(function (products) {
        self.allResults = self.shuffleArray(products || []);
        self.currentPage = 0;
        self.displayResults();
      })
      .catch(function () {
        self.renderEmptyTile('Fehler beim Laden der Produkte');
        BunamoUtils.renderPagination(self.paginationEl, 0, 0, null, null);
      });
  };

  FlavorTree.prototype.displayResults = function () {
    var self = this;
    var totalProducts = this.allResults.length;
    var totalPages = Math.ceil(totalProducts / this.productsPerPage);
    var startIndex = this.currentPage * this.productsPerPage;
    var endIndex = Math.min(startIndex + this.productsPerPage, totalProducts);
    var currentProducts = this.allResults.slice(startIndex, endIndex);

    if (totalProducts === 0) {
      this.renderEmptyTile('Keine Kaffees mit "' + this.selectedFlavor + '" gefunden');
    } else {
      this.productsGrid.innerHTML = currentProducts.map(function (product) {
        return BunamoUtils.renderProductCard(product, {
          highlightFlavor: self.selectedFlavor,
          highlightColor: self.selectedFlavorColor
        });
      }).join('');
    }

    BunamoUtils.renderPagination(
      this.paginationEl,
      this.currentPage,
      totalPages,
      function () { self.prevPage(); },
      function () { self.nextPage(); },
      { alwaysShow: true }
    );

    this.renderAllButton(totalProducts);
  };

  FlavorTree.prototype.renderAllButton = function (totalProducts) {
    if (!this.allButtonEl) return;
    if (!this.selectedFlavor || totalProducts <= 0) {
      this.allButtonEl.innerHTML = '';
      return;
    }

    var gidMap = window.BUNAMO_FLAVOR_GIDS || {};
    var gid = gidMap[normalize(this.selectedFlavor)];
    var filterUrl = gid
      ? '/collections/all?filter.p.m.bunamo.geschmack=' + encodeURIComponent('gid://shopify/Metaobject/' + gid)
      : '/collections/all?q=' + encodeURIComponent(this.selectedFlavor);

    var bgColor = this.selectedFlavorColor || '#1A1A1A';
    var textColor = this.getContrastColor(bgColor);

    this.allButtonEl.innerHTML =
      '<a href="' + filterUrl + '" class="flavor-all-button" style="background-color:' + bgColor + ';color:' + textColor + ';">' +
      'Alle Kaffees mit ' + BunamoUtils.escapeHtml(this.selectedFlavor) +
      '</a>';
  };

  FlavorTree.prototype.nextPage = function () {
    var totalPages = Math.ceil(this.allResults.length / this.productsPerPage);
    if (this.currentPage < totalPages - 1) {
      this.currentPage += 1;
      this.displayResults();
    }
  };

  FlavorTree.prototype.prevPage = function () {
    if (this.currentPage > 0) {
      this.currentPage -= 1;
      this.displayResults();
    }
  };

  document.addEventListener('DOMContentLoaded', function () {
    var containers = document.querySelectorAll('[id^="flavor-tree-container-"]');
    containers.forEach(function (container) {
      var sectionId = container.id.replace('flavor-tree-container-', '');
      new FlavorTree(sectionId);
    });
  });
})();
